/**
 * @file CompaniesFilters.tsx
 * @brief Search + status filter bar for the Companies table.
 *
 * @details
 * Identical UX to `UsersFilters.tsx` — same debounce window, same `"all"`
 * sentinel. Layout is flush `GridCells` for the section-grid shell
 * (29-07-2026).
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — end-to-end GridCell filters
 * @updated 01-10-2026: status became a header pill
 *          (`CompaniesHeaderFilters`); no Clear button. This file's bar is
 *          the search.
 */

import { useEffect, useState } from "react";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import type { SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import type { AdminCompaniesListParams } from "../schemas";

const ALL = "all";

const STATUS_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any status" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "deleted", label: "Deleted" },
];

type StatusFilter = NonNullable<AdminCompaniesListParams["status"]> | typeof ALL;

interface CompaniesFiltersProps {
  value: AdminCompaniesListParams;
  onChange: (next: AdminCompaniesListParams) => void;
}

/** Status as the page header's text pill. */
export function CompaniesHeaderFilters({ value, onChange }: CompaniesFiltersProps) {
  const status: StatusFilter = (value.status ?? ALL) as StatusFilter;
  return (
    <HeaderFilterMenu
      title="Filter by status"
      searchPlaceholder="Search statuses"
      items={STATUS_ITEMS}
      selected={status}
      label={STATUS_ITEMS.find((i) => i.value === status)?.label ?? STATUS_ITEMS[0].label}
      active={status !== ALL}
      onClear={() => onChange({ ...value, status: undefined, page: 1 })}
      onSelect={(v) =>
        onChange({
          ...value,
          status: v === ALL ? undefined : (v as AdminCompaniesListParams["status"]),
          page: 1,
        })
      }
    />
  );
}

/** The search, rendered by the page inside its bottom dock (DES 01-10-2026). */
export function CompaniesFilters({ value, onChange }: CompaniesFiltersProps) {
  const [searchInput, setSearchInput] = useState(value.search ?? "");
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    if ((value.search ?? "") === debouncedSearch) return;
    onChange({ ...value, search: debouncedSearch || undefined, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    // The Notes screen's search (DES 01-10-2026), rendered by the page
    // inside its bottom PageDock ("dock style ki tarha neechay").
    <ExpandingSearch
      value={searchInput}
      onChange={(next) => setSearchInput(next)}
      placeholder="Search by company ID or name"
      label="Search companies"
    />
  );
}
